import React, {useRef, useState, useEffect, useCallback, useMemo} from 'react';
import cx from 'classnames';
import {
    Flex,
    Button,
    Icon,
    PriorityIcon,
    Text,
    HTMLDialog,
    Title
} from '@priority-software/priority-style-react';
import {apiLocker, formsStore} from '../../stores/Form';
import {
    checkKeyId,
    deleteItemFromBuffer,
    errorMsg,
    getBufferData,
    getLastKeyId,
    pasteValueToPosition,
    resetSaveToBuffer,
    setSaveToBuffer
} from './utils';
import {getServerTexts} from './texts';
import text from './texts';
import {useEventListener} from '../useEventListener';
import {keys} from '../../constants/KeyCodes';
import {checkValueOnColumnType} from '../../features/Form/utils/pasteToColumn';
import {getLang, isRTL} from '../../Localization';
import styles from './styles.module.scss';
import {DeleteDialog} from '../../features/Form/components/DeleteDialog';
import {Content} from './Content';
import {HelpDialogF9} from './HelpDialogF9';
import {Dropdown} from './Dropdown';
import {f9DialogId} from './constants';

export type BufferData = {id: string; value: string; desc: string | undefined};

export type Props = {
    componentClasses?: string;
};
//only numbers
const regExp = /^\d+$/;

export const useF9Dialog = ({componentClasses}: Props) => {
    const lang = getLang();
    const form = formsStore.getActiveForm();
    const [keyId, setKeyId] = useState('');
    const [search, setSearch] = useState('');
    const [isOpen, setOpen] = useState(false);
    const [isCopyAction, setCopyAction] = useState(false);
    const [isOpenDropdown, setOpenDropdown] = useState(false);
    const [isOpenDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [isOpenHelpDialog, setOpenHelpDialog] = useState(false);
    const [deletedElement, setDeletedElement] = useState<{element: BufferData; isOpen: boolean}>({
        element: null,
        isOpen: false
    });

    const serverStore = useRef<{
        data: BufferData[] | [];
        lastId: string;
    } | null>(null);
    const clearAllRef = useRef<HTMLButtonElement>(null);
    const [msg, setMsg] = useState('');
    const [dataList, setDataList] = useState<BufferData[]>([]);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [description, setDescription] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(0);

    const value = useRef<string>(null);
    const lastKey = useRef<number>(0);

    useEffect(() => {
        getServerTexts();
    }, []);

    const onSelect = (e: KeyboardEvent) => {
        if (contentRef.current?.contains(e.target as Node)) {
            handleSelect(dataList[focusedIndex]);
        } else if (cancelButtonRef.current === e.target) {
            onClose();
        } else {
            !isCopyAction && handlePasteItem(dataList[focusedIndex]);
        }
    };
    const handleSelect = (item: BufferData) => {
        if (isCopyAction && value.current) {
            keyId && handleAction();
            return;
        }
        handlePasteItem(item);
    };

    const handlerEvent = (e: KeyboardEvent) => {
        if (e.key === keys.ESC) {
            isOpenDeleteDialog && setOpenDeleteDialog(false);
            isOpenHelpDialog && setOpenHelpDialog(false);
            deletedElement.isOpen && setDeletedElement({element: null, isOpen: false});
        }

        if (isOpen && !isOpenDeleteDialog && !deletedElement.isOpen && !isOpenHelpDialog) {
            const hasDataOnPaste = dataList[focusedIndex] || isCopyAction;
            switch (e.key) {
                case keys.ENTER: {
                    if (cancelButtonRef.current === e.currentTarget) {
                        onClose();
                    }
                    if (
                        !(e.target as HTMLDivElement).classList.contains('blockAction') &&
                        hasDataOnPaste
                    ) {
                        onSelect(e);
                    }

                    break;
                }

                case keys.ARROW_DOWN: {
                    setFocusedIndex(prev => (prev === dataList.length - 1 ? 0 : prev + 1));
                    break;
                }
                case keys.ARROW_UP: {
                    setFocusedIndex(prev => (prev === 0 ? dataList.length - 1 : prev - 1));
                    break;
                }
                case keys.ESC: {
                    onClose();

                    break;
                }
                default:
                    break;
            }
        }
    };

    useEventListener('keydown', handlerEvent);

    const openF9Dialog = async (isCopy: boolean) => {
        if (!isOpen) {
            apiLocker.lockSession(form.session, {navigationOnly: true});
            const responseData = await getBufferData();

            if (Array.isArray(responseData.data)) {
                serverStore.current = responseData;
            } else {
                // Check  for data structure if we have old data structure, we clear buffer data
                resetSaveToBuffer();
                serverStore.current = {
                    ...responseData,
                    data: []
                };
            }
            setDataList(serverStore.current?.data || []);
            lastKey.current = getLastKeyId(serverStore.current, isCopy);
            if (isCopy) {
                setOpenDropdown(false);

                const id = checkKeyId(lastKey.current);
                setDescription('');
                setKeyId(id.toString());
                const fieldValue =
                    typeof form.activeField().currentValue == 'string'
                        ? form.activeField().currentValue
                        : '';

                const selection = document.getSelection().toString() || (fieldValue as string);
                value.current = selection;
                const errMsg = errorMsg({
                    data: serverStore.current?.data,
                    value: selection,
                    id: id.toString()
                });
                errMsg.length && setOpenDropdown(true);
                setMsg(errMsg);
            } else {
                setMsg('');
                setOpenDropdown(true);
                setFocusedIndex(0);
                setKeyId(lastKey.current ? lastKey.current.toString() : '1');
            }
            setOpen(true);
            setCopyAction(isCopy);
        }
    };
    const onClose = () => {
        setSearch('');
        setKeyId('');
        apiLocker.unlockSession(form.session);
        setOpenHelpDialog(false);
        setOpen(false);
        form.formActiveElement.focus();
    };

    const handleChangeId = useCallback(
        e => {
            const value = e.target.value;
            const errMsg = errorMsg({data: serverStore.current?.data, id: value});
            errMsg.length && setOpenDropdown(true);
            setMsg(value ? errMsg : text.mandatory_field_error);
            if (regExp.test(e.target.value)) {
                setKeyId(e.target.value === '0' ? '1' : value);
                return;
            }
            setKeyId('');
        },
        [serverStore.current?.data]
    );

    const handleAction = async (item?: BufferData) => {
        setSearch('');
        const notReadOnly = !form?.getColumnByID(form?.activeField()?.columnId)?.isReadonly;
        const activeElement = form.formActiveElement as HTMLInputElement;
        const isSearchModeOrReadOnly = form.searchModeLevel || notReadOnly;
        let mixedValue = '';
        if (isCopyAction && value.current) {
            await setSaveToBuffer(serverStore.current?.data, keyId, value.current, description);
            activeElement.select();
        } else if (isSearchModeOrReadOnly && !isCopyAction) {
            const pasteValue =
                serverStore.current?.data.find(pasteItem => pasteItem.id === item?.id)?.value ||
                dataList[focusedIndex].value;
            const elementValueLength = activeElement.value.length;
            const startPos = activeElement.selectionStart;
            const endPos = activeElement.selectionEnd;
            const column = form?.getColumnByID(form?.activeField()?.columnId);
            mixedValue = pasteValueToPosition(activeElement, pasteValue);
            mixedValue =
                mixedValue.length > column.maxLength
                    ? mixedValue.slice(0, column.maxLength)
                    : mixedValue;
            const isValidValue = checkValueOnColumnType(column, mixedValue, lang);

            if (isValidValue) {
                if (form.searchModeLevel) {
                    form.updateSearchModeCellValue(
                        mixedValue,
                        form.activeField().columnId,
                        form.currentRow
                    );
                } else {
                    form.activeField().setCurrentValue(mixedValue);
                }
                activeElement.value = mixedValue;
                if (elementValueLength === endPos || !elementValueLength) {
                    activeElement.select();
                } else {
                    activeElement.selectionStart = startPos + pasteValue.length;
                    activeElement.selectionEnd = startPos + pasteValue.length;
                }
            }
        }
        apiLocker.unlockSession(form.session);
        setOpen(false);
    };

    const handleOpenDropdown = useCallback(() => {
        setOpenDropdown(prev => !prev);
    }, []);

    const handleDelete = useCallback(async () => {
        const res = await deleteItemFromBuffer(
            serverStore.current?.data,
            deletedElement.element.id
        );
        setMsg('');
        setDataList(res);
        serverStore.current = {...serverStore.current, data: res};
        setDeletedElement({element: null, isOpen: false});
    }, [deletedElement, serverStore.current]);

    const handleSearch = useCallback(e => {
        setSearch(e.target.value);
        setOpenDropdown(true);
        const value = (e.target.value as string).toLocaleLowerCase();

        let data;
        if (value.length && serverStore.current?.data) {
            data = (serverStore.current.data as BufferData[]).reduce(
                (acc: {ids: BufferData[]; other: BufferData[]}, obj: BufferData) => {
                    if (obj.id.toLocaleLowerCase().includes(value)) {
                        acc.ids.push(obj);
                        return acc;
                    }
                    const condition =
                        obj.value.toLocaleLowerCase().includes(value) ||
                        obj.desc.toLocaleLowerCase().includes(value);

                    condition && acc.other.push(obj);

                    return acc;
                },
                {ids: [], other: []}
            );

            data = [...data.ids, ...data.other];
            setFocusedIndex(0);
            return setDataList(data);
        }
        setDataList(serverStore.current?.data || []);
    }, []);

    const handleDeleteAll = useCallback(async () => {
        await resetSaveToBuffer();
        setDataList([]);
        serverStore.current = null;
        setMsg('');
        setOpenDeleteDialog(false);
    }, []);

    const handleDescription = useCallback(e => {
        setDescription(e.target.value);
    }, []);

    const handleCloseDeleteDialog = useCallback(() => {
        setOpenDeleteDialog(false);
        setDeletedElement({element: null, isOpen: false});
    }, []);

    const handlePasteItem = useCallback(
        (item: BufferData) => {
            if (!isCopyAction) {
                handleAction(item);
            }
        },
        [isCopyAction]
    );

    const deleteItemText = (text.delete_msg_text as string)?.replace(
        'X',
        ` ${deletedElement.element?.id} ${deletedElement.element?.value} `
    );
    const clearAllText = useMemo(
        () =>
            (text.delete_all_items_msg as string)?.replace(
                'X',
                `${serverStore.current?.data?.length}`
            ),
        [text, serverStore.current?.data]
    );
    const clearAll = useMemo(
        () => (text.clear_all as string)?.replace('X', `${serverStore.current?.data?.length || 0}`),
        [text, serverStore.current?.data]
    );

    const F9Dialog = isOpen && (
        <>
            <HTMLDialog
                id={f9DialogId}
                toolbarContent={
                    <div dir={isRTL() ? 'rtl' : 'ltr'} className={styles.toolbar}>
                        <Icon
                            skin="standart"
                            onClick={() => setOpenHelpDialog(true)}
                            icon={PriorityIcon.questionCircle}
                            componentClasses={styles.questionIcon}
                        />
                        <div className={styles.separatorBorder}></div>
                    </div>
                }
                componentClasses={cx(styles.wrapper, componentClasses)}
                size="small"
                isOpen={isOpen}
                onClose={onClose}
                title={
                    <Title skin="h2" textColor="primary-700">
                        {isCopyAction ? text?.copy_to_buffer_title : text?.paste_from_buffer_title}
                    </Title>
                }
                content={
                    <div ref={contentRef}>
                        <Content
                            isCopyAction={isCopyAction}
                            keyId={keyId}
                            handleChangeId={handleChangeId}
                            value={value.current}
                            handleDescription={handleDescription}
                            description={description}
                            handleSearch={handleSearch}
                            search={search}
                            message={msg}
                            isOpenDeleteDialog={deletedElement.isOpen}
                        />
                        <Dropdown
                            isCopyAction={isCopyAction}
                            data={dataList}
                            search={search}
                            isOpenDropdown={isOpenDropdown}
                            handleOpenDropdown={handleOpenDropdown}
                            handlePasteItem={handlePasteItem}
                            deleteElement={setDeletedElement}
                            focusedIndex={focusedIndex}
                        />
                    </div>
                }
                footer={
                    <Flex componentClasses={styles.footer}>
                        {dataList.length ? (
                            <Button
                                inputRef={clearAllRef}
                                skin="text"
                                componentClasses={(styles.clearAll, 'blockAction')}
                                onClick={() => setOpenDeleteDialog(true)}
                            >
                                {clearAll}
                            </Button>
                        ) : (
                            ''
                        )}
                        <Button
                            size="medium"
                            skin="secondary"
                            onClick={onClose}
                            inputRef={cancelButtonRef}
                            width="fullWidth"
                            componentClasses="blockAction"
                        >
                            {text.cancel_button}
                        </Button>
                        <Button
                            size="medium"
                            skin="primary"
                            onClick={handleAction}
                            disabled={isCopyAction ? !keyId || !value.current : !dataList.length}
                            width="fullWidth"
                        >
                            {isCopyAction ? text.copy_button : text.paste_button}
                        </Button>
                    </Flex>
                }
            />
            <DeleteDialog
                isOpen={isOpenDeleteDialog}
                onClose={handleCloseDeleteDialog}
                action={handleDeleteAll}
                content={
                    <>
                        <Text skin="paragraph-1" textColor="primary-700">
                            {text.delete}
                        </Text>
                        <Text skin="caption-2" textColor="primary-700">
                            {clearAllText}
                        </Text>
                    </>
                }
            />
            <DeleteDialog
                isOpen={deletedElement.isOpen}
                onClose={handleCloseDeleteDialog}
                action={handleDelete}
                content={
                    <>
                        <Text skin="paragraph-1" textColor="primary-700">
                            {text.delete}
                        </Text>
                        <Text skin="caption-2" textColor="primary-700" tag="p">
                            {deleteItemText}
                        </Text>
                    </>
                }
            />
            <HelpDialogF9
                isOpen={isOpenHelpDialog}
                isCopyAction={isCopyAction}
                action={() => setOpenHelpDialog(false)}
                onClose={() => setOpenHelpDialog(false)}
            />
        </>
    );

    return {F9Dialog, openF9Dialog, isOpen};
};
